import { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ChatCircle } from "@phosphor-icons/react";
import { formatDateIT } from "@/lib/formatDate";
import { OverviewCard, OverviewEmpty } from "./Card";

/**
 * Widget #1 — Grafico andamento check-in.
 * Per ogni risposta settimanale calcoliamo una media degli score (domande 'scale').
 * L'utente può anche scegliere una singola domanda per vederne l'andamento.
 */

function tooltipFormatter({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#171717] border border-[#262626] rounded-lg p-2.5 text-xs shadow-2xl">
      <p className="text-zinc-400 mb-1">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} className="font-medium" style={{ color: entry.color }}>
          {entry.name}: <span className="text-white">{entry.value}</span>
        </p>
      ))}
    </div>
  );
}

export function CheckinChartWidget({ responses, config }) {
  const scaleQuestions = useMemo(
    () => (config?.questions || []).filter((q) => q.type === "scale" && q.enabled),
    [config]
  );

  const [selected, setSelected] = useState("avg");

  const series = useMemo(() => {
    // Responses come from API sorted DESC by week_start — reverse for charts.
    return [...(responses || [])]
      .sort((a, b) => (a.week_start > b.week_start ? 1 : -1))
      .map((r) => {
        const ans = r.answers || {};
        const scaleVals = scaleQuestions
          .map((q) => Number(ans[q.key]))
          .filter((v) => !Number.isNaN(v));
        const avg = scaleVals.length
          ? +(scaleVals.reduce((a, b) => a + b, 0) / scaleVals.length).toFixed(1)
          : null;
        const point = {
          date: formatDateIT(r.week_start),
          avg,
        };
        scaleQuestions.forEach((q) => {
          const v = Number(ans[q.key]);
          point[q.key] = Number.isNaN(v) ? null : v;
        });
        return point;
      });
  }, [responses, scaleQuestions]);

  const activeLine = selected === "avg"
    ? { key: "avg", label: "Media settimanale" }
    : (() => {
      const q = scaleQuestions.find((x) => x.key === selected);
      return q ? { key: q.key, label: q.label } : { key: "avg", label: "Media" };
    })();

  return (
    <OverviewCard
      title="Andamento check-in"
      icon={ChatCircle}
      iconColor="text-[#34C759]"
      testId="overview-checkin-chart"
      action={
        scaleQuestions.length > 1 ? (
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="bg-[#171717] border border-[#262626] rounded-md text-xs text-zinc-300 px-2 py-1 outline-none focus:border-[#007AFF]"
            data-testid="overview-checkin-metric"
          >
            <option value="avg">Media generale</option>
            {scaleQuestions.map((q) => (
              <option key={q.key} value={q.key}>{q.label}</option>
            ))}
          </select>
        ) : null
      }
    >
      {series.length === 0 ? (
        <OverviewEmpty message="Nessun check-in ricevuto. Attiva il check-in settimanale per iniziare a raccogliere dati." />
      ) : (
        <div className="h-[200px] w-full -mx-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
              <XAxis dataKey="date" stroke="#52525B" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 10]} stroke="#52525B" tick={{ fontSize: 10 }} />
              <Tooltip content={tooltipFormatter} />
              <Line
                type="monotone"
                dataKey={activeLine.key}
                name={activeLine.label}
                stroke="#34C759"
                strokeWidth={2}
                dot={{ r: 3, fill: "#34C759" }}
                activeDot={{ r: 5 }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </OverviewCard>
  );
}
